import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { ApiParkingMasterDataService } from '../../../../core/services/api/system-settings/api-parking-master-data.service';
import { ApiService } from '../../../../core/services/api.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { MapperParkingMasterDataService } from '../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ClientIInfo } from '../../../../core/models/parking.model';
import { ParkingVehicleType, ParkingVehicleTypeDto } from '../../../../core/models/system-settings/parking-master-data.model';
import {
  AccessGroupScope,
  mapAccessGroupScope,
} from '../../../../core/models/system-settings/access-group.model';
import {
  AllocateClientFreeQuotaPayload,
  CLIENT_FREE_QUOTA_MANAGED_BY_OPTIONS,
  ClientFreeQuota,
  FreeQuotaActivityEntityTypes,
  SiteFreeQuota,
  UpdateClientFreeQuotaPayload,
  freeQuotaActivityEntityId,
  mapClientFreeQuotaList,
  mapSiteFreeQuotaList,
} from '../../../../core/models/system-settings/free-quota.model';
import { ModalAdminActivityHistoryComponent } from '../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { PssConfirmModalComponent, PssConfirmModalVariant } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';

export type ClientQuotaStatusFilter = 'all' | 'available' | 'near_full' | 'full';
export type ClientQuotaSortColumn = 'client' | 'vehicle' | 'approved' | 'used' | 'remaining' | 'usage';

/** ใช้ ≥ ค่านี้ (%) ถือว่าใกล้เต็ม */
const NEAR_FULL_PERCENT = 80;

/**
 * หน้าฝ่ายอาคาร — แจกสิทธิ์โควต้าจอดฟรีจาก pool ลานให้ Client
 */
@Component({
  selector: 'app-building-free-quota',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    PssLoadingComponent,
    ModalAdminActivityHistoryComponent,
    PssConfirmModalComponent,
  ],
  templateUrl: './building-free-quota.component.html',
  styleUrl: './building-free-quota.component.css',
})
export class BuildingFreeQuotaComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiSystemSettingsService);
  private readonly apiMaster = inject(ApiParkingMasterDataService);
  private readonly apiLegacy = inject(ApiService);
  private readonly masterMapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  readonly managedByOptions = CLIENT_FREE_QUOTA_MANAGED_BY_OPTIONS;
  readonly activityEntityTypes = FreeQuotaActivityEntityTypes;
  readonly pageSizeOptions = [20, 50, 100];
  readonly statusTabs: { value: ClientQuotaStatusFilter; label: string; icon: string }[] = [
    { value: 'all', label: 'ทั้งหมด', icon: 'apps' },
    { value: 'available', label: 'ยังใช้ได้', icon: 'check_circle' },
    { value: 'near_full', label: 'ใกล้เต็ม', icon: 'warning' },
    { value: 'full', label: 'เต็มแล้ว', icon: 'block' },
  ];

  siteQuotas = signal<SiteFreeQuota[]>([]);
  clientQuotas = signal<ClientFreeQuota[]>([]);
  vehicleTypes = signal<ParkingVehicleType[]>([]);
  clients = signal<ClientIInfo[]>([]);
  accessScope = signal<AccessGroupScope | null>(null);
  selectedClientRow = signal<ClientFreeQuota | null>(null);
  isLoading = signal(false);
  isAllocating = signal(false);
  isUpdatingClient = signal(false);
  showActivityHistory = false;
  showAllocateModal = false;
  showEditModal = false;
  showHelp = false;

  clientListSearch = signal('');
  statusFilter = signal<ClientQuotaStatusFilter>('all');
  vehicleFilter = signal(0);
  sortColumn = signal<ClientQuotaSortColumn>('client');
  sortDirection = signal<'asc' | 'desc'>('asc');
  currentPage = signal(1);
  pageSize = signal(20);
  isPageSizeDropdownOpen = false;

  allocateClientId = 0;
  allocateVehicleTypeId = 0;
  allocateAdditionalQuota = 0;
  allocateManagedBy = 'site_building';
  allocateClientSearch = '';
  isAllocateClientOpen = false;

  editClientQuotaId = 0;
  editClientQuotaApproved = 0;
  editClientManagedBy = 'site_building';

  showConfirm = false;
  confirmTitle = 'ยืนยันการดำเนินการ?';
  confirmMessage = '';
  confirmHighlight = '';
  confirmMessageSuffix = 'กด ใช่ เพื่อยืนยัน หรือ ไม่ เพื่อยกเลิก';
  confirmVariant: PssConfirmModalVariant = 'warning';
  confirmIcon = 'help';
  confirmLoading = false;
  private pendingConfirmAction: (() => void) | null = null;

  /** กรองค้นหา + ประเภทรถ (ก่อนแยกสถานะ — ใช้นับ badge ของแต่ละแท็บ) */
  private searchedClientQuotas = computed(() => {
    const q = this.clientListSearch().trim().toLowerCase();
    const vt = this.vehicleFilter();
    return this.clientQuotas().filter((r) => {
      if (vt && r.vehicleTypeId !== vt) return false;
      if (!q) return true;
      const blob = `${r.clientName ?? ''} ${r.vehicleTypeName ?? ''} ${r.clientId}`.toLowerCase();
      return blob.includes(q);
    });
  });

  statusCounts = computed(() => {
    const rows = this.searchedClientQuotas();
    return {
      all: rows.length,
      available: rows.filter((r) => this.rowStatus(r) === 'available').length,
      near_full: rows.filter((r) => this.rowStatus(r) === 'near_full').length,
      full: rows.filter((r) => this.rowStatus(r) === 'full').length,
    } as Record<ClientQuotaStatusFilter, number>;
  });

  filteredClientQuotas = computed(() => {
    const status = this.statusFilter();
    const rows = status === 'all'
      ? this.searchedClientQuotas()
      : this.searchedClientQuotas().filter((r) => this.rowStatus(r) === status);

    const dir = this.sortDirection() === 'asc' ? 1 : -1;
    const col = this.sortColumn();
    return [...rows].sort((a, b) => {
      switch (col) {
        case 'vehicle':
          return dir * (a.vehicleTypeName || '').localeCompare(b.vehicleTypeName || '', 'th');
        case 'approved':
          return dir * (a.quotaApproved - b.quotaApproved);
        case 'used':
          return dir * (a.quotaUsed - b.quotaUsed);
        case 'remaining':
          return dir * (a.quotaRemaining - b.quotaRemaining);
        case 'usage':
          return dir * (this.usagePercent(a) - this.usagePercent(b));
        default:
          return dir * (a.clientName || '').localeCompare(b.clientName || '', 'th');
      }
    });
  });

  totalPages = computed(() => Math.max(1, Math.ceil(this.filteredClientQuotas().length / this.pageSize())));

  pagedClientQuotas = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filteredClientQuotas().slice(start, start + this.pageSize());
  });

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) {
        untracked(() => {
          this.siteQuotas.set([]);
          this.clientQuotas.set([]);
          this.accessScope.set(null);
          this.clearClientEdit();
          this.showAllocateModal = false;
        });
        return;
      }
      untracked(() => this.loadAll());
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get selectedSiteName(): string {
    return this.siteState.selectedSite()?.name ?? '';
  }

  get canUpdate(): boolean {
    if (this.accessScope()?.groupId && !this.accessScope()?.canManageQuota) {
      return false;
    }
    return this.permissionService.canUpdate(this.router.url);
  }

  get isAccessScoped(): boolean {
    return !!(this.accessScope()?.groupId);
  }

  get historyEntityId(): number {
    return freeQuotaActivityEntityId(this.selectedClientRow()?.id);
  }

  /** ประเภทรถที่มี pool ลานเท่านั้น */
  get allocatableVehicleTypes(): ParkingVehicleType[] {
    const allowed = new Set(this.siteQuotas().map((q) => q.vehicleTypeId));
    return this.vehicleTypes().filter((vt) => allowed.has(vt.id));
  }

  /** แท็บประเภทรถบนตาราง — จากประเภทที่มีแถว Client จริง */
  get vehicleTabs(): { id: number; name: string }[] {
    const map = new Map<number, string>();
    for (const r of this.clientQuotas()) {
      if (!map.has(r.vehicleTypeId)) {
        map.set(r.vehicleTypeId, r.vehicleTypeName || `ประเภท #${r.vehicleTypeId}`);
      }
    }
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }

  get allocatePoolRemaining(): number {
    const row = this.siteQuotas().find((q) => q.vehicleTypeId === this.allocateVehicleTypeId);
    return row ? this.poolRemaining(row) : 0;
  }

  get allocateSiteRow(): SiteFreeQuota | undefined {
    return this.siteQuotas().find((q) => q.vehicleTypeId === this.allocateVehicleTypeId);
  }

  get filteredAllocateClients(): ClientIInfo[] {
    const q = this.allocateClientSearch.trim().toLowerCase();
    const all = this.clients();
    if (!q) return all;
    return all.filter((c) => (c.name || '').toLowerCase().includes(q) || String(c.id).includes(q));
  }

  get allocateClientName(): string {
    return this.clients().find((c) => c.id === this.allocateClientId)?.name ?? '';
  }

  /** Client + ประเภทนี้มีแถวอยู่แล้ว → การแจกจะบวกเพิ่มเข้าแถวเดิม */
  get allocateExistingRow(): ClientFreeQuota | undefined {
    if (!this.allocateClientId || !this.allocateVehicleTypeId) return undefined;
    return this.clientQuotas().find(
      (c) => c.clientId === this.allocateClientId && c.vehicleTypeId === this.allocateVehicleTypeId
    );
  }

  get canSubmitAllocate(): boolean {
    return this.canUpdate
      && !this.isAllocating()
      && this.allocateClientId > 0
      && this.allocateVehicleTypeId > 0
      && this.allocateAdditionalQuota > 0
      && this.allocateAdditionalQuota <= this.allocatePoolRemaining;
  }

  get editDelta(): number {
    const row = this.selectedClientRow();
    return row ? (this.editClientQuotaApproved || 0) - row.quotaApproved : 0;
  }

  /** เพิ่มได้สูงสุด = ที่ลานเหลือแจก */
  get editMaxApproved(): number {
    const row = this.selectedClientRow();
    if (!row) return 0;
    const site = this.siteQuotas().find((q) => q.vehicleTypeId === row.vehicleTypeId);
    return row.quotaApproved + (site ? this.poolRemaining(site) : 0);
  }

  get totalApproved(): number {
    return this.siteQuotas().reduce((sum, r) => sum + (r.quotaApproved || 0), 0);
  }

  get totalAllocated(): number {
    return this.siteQuotas().reduce((sum, r) => sum + (r.quotaAllocatedToClients || 0), 0);
  }

  get totalPoolRemaining(): number {
    return this.siteQuotas().reduce((sum, r) => sum + this.poolRemaining(r), 0);
  }

  get totalClientRemaining(): number {
    return this.clientQuotas().reduce((sum, r) => sum + (r.quotaRemaining || 0), 0);
  }

  get startItemIndex(): number {
    return this.filteredClientQuotas().length === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.filteredClientQuotas().length);
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const current = this.currentPage();
    const start = Math.max(1, Math.min(current - 2, total - 4));
    const end = Math.min(total, start + 4);
    const pages: number[] = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  }

  /*#################################################### Function ################################################*/

  ngOnInit(): void {
    this.loadVehicleTypes();
    if (this.selectedSiteId) {
      this.loadAll();
    }
  }

  onRefresh(): void {
    this.clientListSearch.set('');
    this.statusFilter.set('all');
    this.vehicleFilter.set(0);
    this.sortColumn.set('client');
    this.sortDirection.set('asc');
    this.currentPage.set(1);
    this.loadAll();
  }

  onSearchChange(value: string): void {
    this.clientListSearch.set(value);
    this.currentPage.set(1);
  }

  setStatusFilter(value: ClientQuotaStatusFilter): void {
    this.statusFilter.set(value);
    this.currentPage.set(1);
  }

  setVehicleFilter(id: number): void {
    this.vehicleFilter.set(id);
    this.currentPage.set(1);
  }

  toggleSort(column: ClientQuotaSortColumn): void {
    if (this.sortColumn() === column) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set(column === 'client' || column === 'vehicle' ? 'asc' : 'desc');
    }
    this.currentPage.set(1);
  }

  sortIcon(column: ClientQuotaSortColumn): string {
    if (this.sortColumn() !== column) return 'unfold_more';
    return this.sortDirection() === 'asc' ? 'expand_less' : 'expand_more';
  }

  setPage(page: number): void {
    if (page < 1 || page > this.totalPages()) return;
    this.currentPage.set(page);
  }

  togglePageSizeDropdown(): void {
    this.isPageSizeDropdownOpen = !this.isPageSizeDropdownOpen;
  }

  closePageSizeDropdown(): void {
    setTimeout(() => (this.isPageSizeDropdownOpen = false), 150);
  }

  selectPageSize(size: number): void {
    this.pageSize.set(size);
    this.currentPage.set(1);
    this.isPageSizeDropdownOpen = false;
  }

  startAllocate(vehicleTypeId?: number): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แจกโควต้า');
      return;
    }
    if (!this.siteQuotas().length) {
      this.toast.error('ยังไม่มีโควต้าลาน — ให้ Admin ตั้งที่เมนูโควต้าจอดฟรี (ลาน) ก่อน');
      return;
    }
    this.clearClientEdit();
    this.allocateClientId = 0;
    this.allocateAdditionalQuota = 0;
    this.allocateManagedBy = 'site_building';
    this.allocateClientSearch = '';
    this.allocateVehicleTypeId = vehicleTypeId ?? 0;
    if (!this.allocateVehicleTypeId && this.allocatableVehicleTypes.length === 1) {
      this.allocateVehicleTypeId = this.allocatableVehicleTypes[0].id;
    }
    this.showAllocateModal = true;
  }

  cancelAllocate(): void {
    if (this.isAllocating()) return;
    this.showAllocateModal = false;
    this.allocateAdditionalQuota = 0;
    this.allocateClientSearch = '';
    this.isAllocateClientOpen = false;
  }

  selectAllocateVehicle(id: number): void {
    if (!this.canUpdate || this.isAllocating()) return;
    this.allocateVehicleTypeId = id;
    if (this.allocateAdditionalQuota > this.allocatePoolRemaining) {
      this.allocateAdditionalQuota = this.allocatePoolRemaining;
    }
  }

  stepAllocate(delta: number): void {
    const next = (this.allocateAdditionalQuota || 0) + delta;
    this.allocateAdditionalQuota = Math.max(0, Math.min(next, this.allocatePoolRemaining));
  }

  fillAllocateMax(): void {
    this.allocateAdditionalQuota = this.allocatePoolRemaining;
  }

  openAllocateClientList(): void {
    if (!this.canUpdate || this.isAllocating()) return;
    this.allocateClientSearch = '';
    this.isAllocateClientOpen = true;
  }

  closeAllocateClientList(): void {
    setTimeout(() => {
      this.isAllocateClientOpen = false;
      this.allocateClientSearch = '';
    }, 150);
  }

  selectAllocateClient(client: ClientIInfo): void {
    this.allocateClientId = client.id;
    this.allocateClientSearch = '';
    this.isAllocateClientOpen = false;
  }

  clearAllocateClient(): void {
    this.allocateClientId = 0;
    this.allocateClientSearch = '';
  }

  selectClientRow(row: ClientFreeQuota): void {
    this.selectedClientRow.set(row);
    this.editClientQuotaId = row.id;
    this.editClientQuotaApproved = row.quotaApproved;
    this.editClientManagedBy = row.managedBy || 'site_building';
  }

  openEdit(row: ClientFreeQuota): void {
    this.selectClientRow(row);
    this.showAllocateModal = false;
    this.showEditModal = true;
  }

  closeEdit(): void {
    if (this.isUpdatingClient()) return;
    this.showEditModal = false;
    this.clearClientEdit();
  }

  stepEdit(delta: number): void {
    const row = this.selectedClientRow();
    if (!row) return;
    const next = (this.editClientQuotaApproved || 0) + delta;
    this.editClientQuotaApproved = Math.max(row.quotaUsed, Math.min(next, this.editMaxApproved));
  }

  clearClientEdit(): void {
    this.selectedClientRow.set(null);
    this.editClientQuotaId = 0;
    this.editClientQuotaApproved = 0;
    this.editClientManagedBy = 'site_building';
  }

  openHistory(row: ClientFreeQuota, event?: Event): void {
    event?.stopPropagation();
    this.selectClientRow(row);
    if (this.historyEntityId <= 0) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
    if (!this.showEditModal) {
      this.clearClientEdit();
    }
  }

  poolRemaining(row: SiteFreeQuota): number {
    return Math.max(0, row.quotaApproved - row.quotaAllocatedToClients);
  }

  /** % ที่ลานแจกให้ Client ไปแล้ว */
  sitePoolPercent(row: SiteFreeQuota): number {
    if (!row.quotaApproved) return 0;
    return Math.min(100, Math.round((row.quotaAllocatedToClients / row.quotaApproved) * 100));
  }

  usagePercent(row: ClientFreeQuota): number {
    if (!row.quotaApproved) return row.quotaUsed > 0 ? 100 : 0;
    return Math.min(100, Math.round((row.quotaUsed / row.quotaApproved) * 100));
  }

  rowStatus(row: ClientFreeQuota): Exclude<ClientQuotaStatusFilter, 'all'> {
    if (row.quotaRemaining <= 0) return 'full';
    return this.usagePercent(row) >= NEAR_FULL_PERCENT ? 'near_full' : 'available';
  }

  usageBarClass(row: ClientFreeQuota): string {
    const status = this.rowStatus(row);
    if (status === 'full') return 'bg-rose-500';
    if (status === 'near_full') return 'bg-amber-400';
    return 'bg-emerald-500';
  }

  statusBadgeClass(row: ClientFreeQuota): string {
    const status = this.rowStatus(row);
    if (status === 'full') return 'bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-300';
    if (status === 'near_full') return 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300';
    return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
  }

  statusLabel(row: ClientFreeQuota): string {
    const status = this.rowStatus(row);
    if (status === 'full') return 'เต็มแล้ว';
    if (status === 'near_full') return 'ใกล้เต็ม';
    return 'ยังใช้ได้';
  }

  managedByLabel(value: string): string {
    if (value === 'site_building') return 'ฝ่ายอาคาร';
    if (value === 'client_self') return 'ผู้เช่าจัดการเอง';
    return this.managedByOptions.find((o) => o.value === value)?.label || value || '—';
  }

  vehicleIcon(name?: string | null): string {
    const n = (name || '').toLowerCase();
    return n.includes('จักรยาน') || n.includes('moto') || n.includes('จยย') ? 'two_wheeler' : 'directions_car';
  }

  trackSite(_: number, row: SiteFreeQuota): number {
    return row.id;
  }

  trackClient(_: number, row: ClientFreeQuota): number {
    return row.id;
  }

  onAllocate(): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์บันทึก');
      return;
    }
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.toast.error('กรุณาเลือกลาน');
      return;
    }
    if (!this.allocateVehicleTypeId) {
      this.toast.error('กรุณาเลือกประเภทรถ');
      return;
    }
    if (!this.allocateClientId) {
      this.toast.error('กรุณาเลือก Client / ผู้เช่า');
      return;
    }
    if (this.allocateAdditionalQuota <= 0) {
      this.toast.error('จำนวนสิทธิ์ที่แจกต้องมากกว่า 0');
      return;
    }
    if (this.allocateAdditionalQuota > this.allocatePoolRemaining) {
      this.toast.error(`แจกได้ไม่เกิน ${this.allocatePoolRemaining} (เหลือใน pool ลาน)`);
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const clientName = this.allocateClientName || String(this.allocateClientId);
    const vtName = this.vehicleTypes().find((v) => v.id === this.allocateVehicleTypeId)?.name
      || String(this.allocateVehicleTypeId);

    const payload: AllocateClientFreeQuotaPayload = {
      userId,
      menuId,
      siteId,
      clientId: this.allocateClientId,
      vehicleTypeId: this.allocateVehicleTypeId,
      additionalQuota: this.allocateAdditionalQuota,
      managedBy: this.allocateManagedBy || 'site_building',
    };

    this.askConfirm({
      title: 'ยืนยันแจกสิทธิ์โควต้า?',
      message: 'จำนวนนี้จะหักจากโควต้าลาน แล้วให้ Client ใช้ต่อการออกบัตรจอดฟรี',
      highlight: `${clientName} · ${vtName} · +${payload.additionalQuota}`,
      variant: 'warning',
      icon: 'card_giftcard',
    }, () => {
      this.isAllocating.set(true);
      this.confirmLoading = true;
      this.api.allocateClientFreeQuota(payload).subscribe({
        next: (res) => {
          if (res?.isSuccess ?? res?.IsSuccess) {
            this.toast.success(res?.message ?? res?.Message ?? 'แจกสิทธิ์สำเร็จ');
            this.allocateAdditionalQuota = 0;
            this.showAllocateModal = false;
            this.loadAll();
          } else {
            this.toast.error(res?.message ?? res?.Message ?? 'แจกสิทธิ์ไม่สำเร็จ');
          }
          this.isAllocating.set(false);
          this.confirmLoading = false;
          this.showConfirm = false;
        },
        error: () => {
          this.toast.error('เกิดข้อผิดพลาดขณะแจกสิทธิ์');
          this.isAllocating.set(false);
          this.confirmLoading = false;
          this.showConfirm = false;
        },
      });
    });
  }

  reclaimUnusedClientQuota(row: ClientFreeQuota): void {
    if (row.quotaRemaining <= 0) {
      this.toast.error('ไม่มีส่วนเหลือให้คืน');
      return;
    }
    this.askConfirm({
      title: 'คืนสิทธิ์ที่ยังไม่ใช้กลับลาน?',
      message: 'จะลดโควต้า Client เหลือเท่าที่ใช้แล้ว และคืนส่วนที่เหลือกลับ pool ลาน',
      highlight: `${row.clientName || row.clientId} · คืน ${row.quotaRemaining}`,
      variant: 'warning',
      icon: 'undo',
    }, () => {
      this.selectClientRow(row);
      this.editClientQuotaApproved = row.quotaUsed;
      this.executeUpdateClientQuota();
    });
  }

  cancelClientQuota(row: ClientFreeQuota): void {
    if (row.quotaUsed > 0) {
      this.askConfirm({
        title: 'ยกเลิกทั้งแถวไม่ได้',
        message: `มีการออกบัตรใช้ไปแล้ว ${row.quotaUsed} — จะคืนเฉพาะสิทธิ์ที่ยังไม่ใช้กลับลานแทน`,
        highlight: `${row.clientName || row.clientId} · คืน ${row.quotaRemaining}`,
        variant: 'warning',
        icon: 'warning',
      }, () => {
        this.selectClientRow(row);
        this.editClientQuotaApproved = row.quotaUsed;
        this.executeUpdateClientQuota();
      });
      return;
    }

    this.askConfirm({
      title: 'ยกเลิกสิทธิ์โควต้า Client?',
      message: 'จะลบแถวนี้และคืนจำนวนทั้งหมดกลับ pool ลาน',
      highlight: String(row.clientName || row.clientId),
      variant: 'danger',
      icon: 'delete_forever',
    }, () => {
      this.selectClientRow(row);
      this.editClientQuotaApproved = 0;
      this.executeUpdateClientQuota();
    });
  }

  onUpdateClientQuota(): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์บันทึก');
      return;
    }
    if (!this.editClientQuotaId) {
      this.toast.error('เลือกแถว Client ก่อน');
      return;
    }

    const row = this.clientQuotas().find((c) => c.id === this.editClientQuotaId);
    if (row && this.editClientQuotaApproved < row.quotaUsed) {
      this.toast.error(`ลดได้ไม่ต่ำกว่าที่ใช้แล้ว (${row.quotaUsed})`);
      return;
    }
    if (row && this.editClientQuotaApproved > this.editMaxApproved) {
      this.toast.error(`เพิ่มได้สูงสุด ${this.editMaxApproved} (ตามที่ลานเหลือแจก)`);
      return;
    }

    const delta = row ? this.editClientQuotaApproved - row.quotaApproved : 0;
    const actionLabel =
      !row ? 'บันทึกโควต้า Client'
        : delta < 0 ? `ลดสิทธิ์ ${-delta} และคืนลาน`
          : delta > 0 ? `เพิ่มสิทธิ์ ${delta} (หักจากลาน)`
            : 'บันทึกการแก้ไข';

    this.askConfirm({
      title: 'ยืนยันปรับสิทธิ์โควต้า Client?',
      message: actionLabel,
      highlight: row?.clientName || String(this.editClientQuotaId),
      variant: delta < 0 ? 'danger' : 'warning',
      icon: delta < 0 ? 'trending_down' : 'save',
    }, () => this.executeUpdateClientQuota());
  }

  private executeUpdateClientQuota(): void {
    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      this.showConfirm = false;
      return;
    }

    const payload: UpdateClientFreeQuotaPayload = {
      userId,
      menuId,
      id: this.editClientQuotaId,
      quotaApproved: this.editClientQuotaApproved,
      managedBy: this.editClientManagedBy,
    };

    this.isUpdatingClient.set(true);
    this.confirmLoading = true;
    this.api.updateClientFreeQuota(payload).subscribe({
      next: (res) => {
        if (res?.isSuccess ?? res?.IsSuccess) {
          this.toast.success(res?.message ?? res?.Message ?? 'อัปเดตสำเร็จ');
          this.showEditModal = false;
          this.clearClientEdit();
          this.loadAll();
        } else {
          this.toast.error(res?.message ?? res?.Message ?? 'อัปเดตไม่สำเร็จ');
        }
        this.isUpdatingClient.set(false);
        this.confirmLoading = false;
        this.showConfirm = false;
      },
      error: () => {
        this.toast.error('เกิดข้อผิดพลาดขณะอัปเดต');
        this.isUpdatingClient.set(false);
        this.confirmLoading = false;
        this.showConfirm = false;
      },
    });
  }

  askConfirm(
    opts: {
      title: string;
      message: string;
      highlight?: string;
      variant?: PssConfirmModalVariant;
      icon?: string;
    },
    action: () => void
  ): void {
    this.confirmTitle = opts.title;
    this.confirmMessage = opts.message;
    this.confirmHighlight = opts.highlight ?? '';
    this.confirmVariant = opts.variant ?? 'warning';
    this.confirmIcon = opts.icon ?? 'help';
    this.confirmLoading = false;
    this.pendingConfirmAction = action;
    this.showConfirm = true;
  }

  onConfirmYes(): void {
    const action = this.pendingConfirmAction;
    this.pendingConfirmAction = null;
    action?.();
  }

  onConfirmNo(): void {
    if (this.confirmLoading) return;
    this.showConfirm = false;
    this.pendingConfirmAction = null;
  }

  /*############################################# API #################################################*/

  private loadAll(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) return;

    this.isLoading.set(true);
    this.loadAccessScope(siteId);

    this.apiLegacy.GetClientIInfo(siteId).subscribe({
      next: (res) => {
        let list = res ?? [];
        const allowed = this.accessScope()?.clientIds;
        if (allowed != null) {
          const set = new Set(allowed);
          list = list.filter((c) => set.has(c.id));
        }
        this.clients.set(list);
      },
      error: () => this.clients.set([]),
    });

    this.api.getSiteFreeQuotas(siteId).subscribe({
      next: (res) => {
        this.siteQuotas.set(mapSiteFreeQuotaList(res));
        this.isLoading.set(false);
      },
      error: () => {
        this.siteQuotas.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดโควต้าลานล้มเหลว');
      },
    });

    this.api.getClientFreeQuotas(siteId).subscribe({
      next: (res) => {
        let list = mapClientFreeQuotaList(res);
        const allowed = this.accessScope()?.clientIds;
        if (allowed != null) {
          const set = new Set(allowed);
          list = list.filter((c) => set.has(c.clientId));
        }
        this.clientQuotas.set(list);
        if (this.currentPage() > this.totalPages()) {
          this.currentPage.set(1);
        }
      },
      error: () => this.clientQuotas.set([]),
    });
  }

  private loadAccessScope(siteId: number): void {
    const userId = this.resolveUserId();
    if (!userId || !siteId) {
      this.accessScope.set(null);
      return;
    }
    this.api.getMyAccessGroupScope(userId, siteId).subscribe({
      next: (res) => {
        const scope = mapAccessGroupScope(res);
        this.accessScope.set(scope);
        if (scope?.clientIds != null) {
          const set = new Set(scope.clientIds);
          this.clients.update((list) => list.filter((c) => set.has(c.id)));
          this.clientQuotas.update((list) => list.filter((c) => set.has(c.clientId)));
        }
      },
      error: () => this.accessScope.set(null),
    });
  }

  private loadVehicleTypes(): void {
    this.apiMaster.getParkingVehicleTypeForTable({ page: 1, pageSize: 200 }).subscribe({
      next: (res) => {
        const mapped = this.masterMapper.mapTable<ParkingVehicleTypeDto, ParkingVehicleType>(
          res,
          (item) => this.masterMapper.mapVehicleType(item)
        );
        this.vehicleTypes.set(mapped.data);
      },
      error: () => this.vehicleTypes.set([]),
    });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }
}
